import { useCallback, useEffect, useState } from "react";

import { authFetch } from "@/api/client";
import { useAuth, type Role } from "@/auth/AuthContext";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { IconSearch } from "@/components/Icon";
import { Pill } from "@/components/Pill";
import { useStore } from "@/state/store";

interface UserWithRole {
  id: string;
  email: string;
  country: string;
  kyc_tier: number;
  role: Role;
  created_at: string;
}

interface UsersResponse {
  items: UserWithRole[];
  next_cursor?: string | null;
}

/** Mappa il ruolo verso la tonalità della Pill. `admin` è il più visibile, di
 *  conseguenza tono warn; operation è accent; user è neutral. */
const ROLE_TONE: Record<Role, "neutral" | "accent" | "warn"> = {
  user: "neutral",
  operation: "accent",
  admin: "warn",
};

/**
 * Lista paginata di tutti gli utenti registrati.
 *
 * Le azioni di modifica ruolo sono visibili solo agli admin. Il bottone non
 * viene mostrato per gli altri ruoli, e il backend rifiuta comunque la chiamata
 * (defense-in-depth).
 *
 * Note implementative:
 *  - Caricamento client-side fino a 100 record con possibilità di filtro per
 *    email. Per dataset reali servirà server-side search (cursor + query),
 *    ma l'endpoint attuale non lo espone.
 *  - Empty state distinto: nessun utente nel sistema vs filtro vuoto.
 */
export function UsersPage() {
  const auth = useAuth();
  const showToast = useStore((s) => s.showToast);

  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<UserWithRole | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await authFetch("/v1/admin/users?limit=100");
      if (!res.ok) throw new Error(`users ${res.status}`);
      const data = (await res.json()) as UsersResponse;
      setUsers(data.items ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "errore sconosciuto");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = search
    ? users.filter((u) =>
        u.email.toLowerCase().includes(search.toLowerCase().trim()),
      )
    : users;

  const columns: DataTableColumn<UserWithRole>[] = [
    {
      key: "email",
      header: "Email",
      cell: (u) => <span className="text-text">{u.email}</span>,
      sortValue: (u) => u.email.toLowerCase(),
    },
    {
      key: "country",
      header: "Paese",
      cell: (u) => (
        <span className="font-mono text-[11.5px] text-text-2">
          {u.country || "—"}
        </span>
      ),
      sortValue: (u) => u.country,
    },
    {
      key: "kyc_tier",
      header: "KYC",
      cell: (u) => <Pill tone="neutral">T{u.kyc_tier}</Pill>,
      sortValue: (u) => u.kyc_tier,
    },
    {
      key: "role",
      header: "Ruolo",
      cell: (u) => <Pill tone={ROLE_TONE[u.role]}>{u.role}</Pill>,
      sortValue: (u) => u.role,
    },
    {
      key: "created_at",
      header: "Registrato",
      cell: (u) => (
        <span className="font-mono text-[11.5px] text-muted">
          {formatDate(u.created_at)}
        </span>
      ),
      sortValue: (u) => new Date(u.created_at),
    },
  ];

  if (auth.role === "admin") {
    columns.push({
      key: "actions",
      header: "Azioni",
      align: "right",
      cell: (u) => (
        <button
          type="button"
          className="btn-secondary py-1 px-3 text-[11.5px]"
          onClick={(e) => {
            e.stopPropagation();
            setEditing(u);
          }}
        >
          Cambia ruolo
        </button>
      ),
    });
  }

  return (
    <div className="flex flex-col gap-4 max-w-[1400px]">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold">Utenti</h1>
          <p className="text-[13px] text-muted mt-1">
            Anagrafica utenti registrati. Solo gli admin possono modificare i
            ruoli.
          </p>
        </div>
        <div className="relative w-[280px]">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted">
            <IconSearch size={14} />
          </span>
          <input
            className="input pl-8"
            placeholder="Cerca email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </header>

      {err && (
        <div className="card text-[12.5px] text-no">
          Caricamento utenti fallito: {err}
        </div>
      )}

      {loading ? (
        <div className="card text-muted text-[13px]">Caricamento…</div>
      ) : (
        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(u) => u.id}
          pageSize={25}
          empty={
            <div className="px-4 py-8 text-center text-muted text-[13px]">
              {search
                ? `Nessun utente con email "${search}".`
                : "Nessun utente registrato."}
            </div>
          }
        />
      )}

      {editing && (
        <ChangeRoleModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={(newRole) => {
            setEditing(null);
            showToast({
              kind: "success",
              message: `Ruolo aggiornato a "${newRole}" per ${editing.email}.`,
            });
            load();
          }}
        />
      )}
    </div>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

/** Modal di modifica ruolo. PUT idempotente: se l'utente seleziona lo stesso
 *  ruolo corrente la chiamata va comunque a buon fine (no-op lato backend). */
function ChangeRoleModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserWithRole;
  onClose: () => void;
  onSaved: (newRole: Role) => void;
}) {
  const showToast = useStore((s) => s.showToast);
  const [role, setRole] = useState<Role>(user.role);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await authFetch(`/v1/admin/users/${user.id}/role`, {
        method: "PUT",
        body: JSON.stringify({ role }),
      });
      if (!res.ok) {
        let detail: string | null = null;
        try {
          const body = await res.json();
          detail = body.detail ?? body.message ?? null;
        } catch {
          /* body non JSON */
        }
        throw new Error(detail ?? `Aggiornamento fallito (${res.status})`);
      }
      onSaved(role);
    } catch (e) {
      showToast({
        kind: "error",
        message: e instanceof Error ? e.message : "errore",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-[420px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-semibold">Cambia ruolo</h2>
          <button
            type="button"
            className="text-muted hover:text-text text-[18px] leading-none"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
        <div className="mb-4 text-[12.5px]">
          <div className="text-muted mb-1">Utente</div>
          <div className="text-text">{user.email}</div>
          <div className="font-mono text-[11px] text-muted mt-1">{user.id}</div>
        </div>
        <label className="flex flex-col gap-1 mb-4">
          <span className="text-[11.5px] text-muted">Nuovo ruolo</span>
          <select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            disabled={saving}
          >
            <option value="user">user</option>
            <option value="operation">operation</option>
            <option value="admin">admin</option>
          </select>
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Annulla
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Salvataggio…" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}
